import { useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import { IMosaicBlock } from '@features/cms/typescript';
import styles from '@features/cms/components/pages/MosaicBlock/MosaicBlock.styles';
import Heading from '@components/Heading/Heading';
import VideoPlayer from '@components/Video/VideoPlayer';
import sanitizeHtml from '@utils/sanitizeHtml';
import useIntersect from '@hooks/useIntersect';
import Button from '@components/Button/Button';
import scrollToElementById from '@utils/scrollToElementById';
import theme from '@styles/theme';
import getButtonHref from '@utils/getButtonHref';
import { SANITIZE_HTML_CONFIG } from '@const/index';
import ResponsiveImage from '@components/ResponsiveImage/ResponsiveImage';
type PropsType = {
  data: IMosaicBlock;
  defaultBackground?: string;
  isFirst?: boolean;
};
const MosaicBlock = ({
  data,
  defaultBackground = theme.rawColors.beige500,
  isFirst = false
}: PropsType) => {
  const [play, setPlay] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const {
    backgroundColor,
    reverse,
    subTitle,
    title,
    description,
    lineSpacing,
    textColor,
    video,
    thumbnailsWithBlur,
    image,
    code,
    buttonStyle
  } = data;
  const variant = data?.variant ? data.variant : 'default';
  const renderMedia = () => {
    if (video) {
      return <VideoPlayer video={video} loop playVideo={play} poster={thumbnailsWithBlur} />;
    }
    if (image) {
      return <ResponsiveImage image={typeof image === 'string' ? image : image.path} alt={typeof image !== 'string' ? image.metadata.alt || subTitle : subTitle} />;
    }
    return null;
  };
  const entry = useIntersect(ref, {
    rootMargin: '-100px',
    threshold: 0.5,
    onlyOnce: true
  });
  const getButton = (): JSX.Element | null => {
    if (data?.buttonText && data?.buttonLink?.url && data?.buttonLink?.url.slice(0, 1) === '#') {
      return <Button onClick={() => data?.buttonLink?.url ? scrollToElementById(data?.buttonLink?.url.substring(1)) : null} variant={buttonStyle !== 'outlinedGraphite' && buttonStyle !== 'outlinedWhite' ? 'default' : 'outlined'} variantColor={buttonStyle !== 'outlinedGraphite' && buttonStyle !== 'solidGraphite' ? 'white' : 'dark'} size="small" cy={`${code}-button`} data-testid="mosaic-block-button">
          {data.buttonText}
        </Button>;
    }
    if (data?.buttonText && getButtonHref(data.buttonLink)) {
      return <Link href={getButtonHref(data.buttonLink)} data-testid="mosaic-block-button-link">
          <Button variant="outlined" variantColor="dark" size="small" cy={`${code}-button`} data-testid="mosaic-block-button">
            {data.buttonText}
          </Button>
        </Link>;
    }
    if (data?.buttonText) {
      return <Button variant="outlined" variantColor="dark" size="small" cy={`${code}-button`} data-testid="mosaic-block-button">
          {data.buttonText}
        </Button>;
    }
    return null;
  };
  const renderSubHeading = (): JSX.Element | null => {
    if (subTitle !== '' && subTitle !== null) {
      if (variant === 'other') {
        return <Heading h={1} pb={16} headingType={4} mobile>
            {subTitle}
          </Heading>;
      }
      return <Heading h={4} pb={16} headingType={4} uppercase mobile>
          {subTitle}
        </Heading>;
    }
    return null;
  };
  useEffect(() => {
    if (entry?.isIntersecting) {
      setPlay(true);
    }
  }, [entry]);
  return <section id={code} sx={styles.mosaicBlock(backgroundColor || defaultBackground || theme.rawColors.beige500, reverse, textColor)} ref={ref} data-testid={code}>
      <div sx={styles.contentBlock} data-testid="mosaic-block-content-block">
        <div data-testid="mosaic-block-content">
          {renderSubHeading()}
          <Heading h={isFirst ? 1 : 2} display={variant === 'default' ? 2 : 3} pb={16} mobile>
            <span dangerouslySetInnerHTML={sanitizeHtml(title, SANITIZE_HTML_CONFIG)} sx={styles.textColor(textColor)} data-testid="mosaic-block-title" />
          </Heading>
          <div dangerouslySetInnerHTML={sanitizeHtml(description, SANITIZE_HTML_CONFIG)} sx={styles.textColor(textColor, lineSpacing)} data-testid="mosaic-block-description" />
          {getButton()}
        </div>
      </div>
      <div sx={styles.imageContent} data-testid="mosaic-block-image-content">
        {renderMedia()}
      </div>
    </section>;
};
export default MosaicBlock;